/**
 * AI Copilot Panel Component
 * Collapsible right-side panel showing module intelligence and quick actions
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../config/msalConfig';
import { useLocation } from 'react-router-dom';
import {
    Drawer,
    Card,
    Space,
    Button,
    Tag,
    Tooltip,
    Typography,
    Divider,
    Collapse,
    List,
    Badge,
    Progress,
    Empty,
    Spin,
    message,
    Alert,
    Statistic,
    Row,
    Col,
    Tabs
} from 'antd';
import {
    RobotOutlined,
    ThunderboltFilled,
    SendOutlined,
    LineChartOutlined,
    BulbOutlined,
    CloseOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    TrophyOutlined,
    AlertOutlined,
    DoubleRightOutlined,
    DashboardOutlined,
    FireOutlined,
    RiseOutlined,
    FallOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
    CommentOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import GaugeChart from 'react-gauge-chart';
import { getModuleHealthScores, triggerManualDigest, getModuleDigest, generateModuleDashboard } from '../services/digestService';
import { useUser } from './UserContext';
import AIDashboardReport from './AIDashboardReport';
import AICopilotChatV2 from '../views/AICopilot/AICopilotChatV2';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const CopilotButton = styled(Button)`
    position: fixed;
    right: 4px;
    top: 47%;
    transform: translateY(-50%);
    z-index: 1000;
    border-radius: 12px 0 0 12px;
    height: 66px;
    width: 66px;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    transition: all 0.3s ease;
    overflow: hidden;

    &:hover {
        transform: translateY(-50%);
    }
`;

const CopilotLottie = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: 78, height: 78 }}>
        <BulbOutlined style={{ fontSize: 28, color: '#f88109' }} />
        <FileTextOutlined style={{ fontSize: 26, color: '#2b3a8a' }} />
    </div>
);

const StyledDrawer = styled(Drawer)`
    .ant-drawer-body {
        padding: 0;
        background: #fafafa;
        display: flex;
        flex-direction: column;
        height: 100%;
    }
`;

const StyledTabs = styled(Tabs)`
    height: 100%;
    display: flex;
    flex-direction: column;
    
    .ant-tabs-nav {
        margin-bottom: 0 !important;
        background: white;
        padding: 0 12px;
        border-bottom: 1px solid #f0f0f0;
    }
    
    .ant-tabs-content-holder {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: white;
    }
    
    .ant-tabs-content {
        height: 100%;
        display: flex;
        flex-direction: column;
    }
    
    .ant-tabs-tabpane {
        height: 100%;
        display: none;
        flex-direction: column;
        overflow: hidden;
        
        &.ant-tabs-tabpane-active {
            display: flex;
        }
    }
`;

const PanelSection = styled(Card)`
    margin: 12px;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const SidebarCollapse = styled(Collapse)`
    background: transparent;
    border: none;
    margin-top: 0;
    box-sizing: border-box;

    .ant-collapse-item {
        background: transparent;
        border-radius: 0;
        margin-bottom: 0;
        border: none;
        border-bottom: 1px solid #f0f0f0;
        overflow: hidden;
        transition: all 0.2s ease;

        &:last-child {
            border-bottom: none;
        }
    }

    .ant-collapse-header {
        padding: 16px 0 !important;
        font-weight: 600;
        font-size: 16px;
        background: transparent !important;
        transition: all 0.2s ease;
        color: #333 !important;

        &:hover {
            color: #F16622 !important;
        }

        .ant-collapse-arrow {
            color: #999 !important;
        }
    }

    .ant-collapse-content-box {
        padding: 0 0 16px 0;
        box-sizing: border-box;
    }
`;

const SidebarSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .title {
        display: flex;
        align-items: center;
        gap: 6px;

        .icon {
            font-size: 15px;
        }
    }
`;

const DashboardGrid = styled.div`
    display: ${props => props.expanded ? 'grid' : 'block'};
    grid-template-columns: ${props => props.expanded ? 'repeat(3, 1fr)' : '1fr'};
    grid-template-rows: ${props => props.expanded ? 'auto auto' : 'auto'};
    gap: ${props => props.expanded ? '24px' : '0'};
    padding: ${props => props.expanded ? '24px' : '0'};
    background: ${props => props.expanded ? '#f5f5f5' : 'transparent'};
`;

const DashboardSection = styled(Card)`
    height: ${props => props.expanded ? 'auto' : 'auto'};
    border-radius: ${props => props.expanded ? '16px' : '8px'};
    box-shadow: ${props => props.expanded ? '0 8px 24px rgba(0, 0, 0, 0.12)' : '0 1px 4px rgba(0, 0, 0, 0.08)'};
    margin: ${props => props.expanded ? '0' : '12px'};
    transition: all 0.3s ease;
    border: ${props => props.expanded ? '2px solid #f0f0f0' : '1px solid #f0f0f0'};
    background: white;

    &:hover {
        box-shadow: ${props => props.expanded ? '0 12px 32px rgba(0, 0, 0, 0.18)' : '0 2px 8px rgba(0, 0, 0, 0.12)'};
        transform: ${props => props.expanded ? 'translateY(-4px)' : 'none'};
    }

    .ant-card-head {
        background: ${props => props.expanded ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'};
        color: ${props => props.expanded ? 'white' : 'inherit'};
        border-bottom: ${props => props.expanded ? 'none' : '1px solid #f0f0f0'};
        border-radius: ${props => props.expanded ? '14px 14px 0 0' : '6px 6px 0 0'};
        padding: ${props => props.expanded ? '16px 24px' : '12px 16px'};

        .ant-card-head-title {
            color: ${props => props.expanded ? 'white' : 'inherit'};
            font-size: ${props => props.expanded ? '18px' : '14px'};
            font-weight: 700;
        }
    }

    .ant-card-body {
        padding: ${props => props.expanded ? '24px' : '16px'};
    }
`;

const MetricCard = styled.div`
    background: linear-gradient(135deg, ${props => props.color}15 0%, ${props => props.color}25 100%);
    border-left: 6px solid ${props => props.color};
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 16px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px ${props => props.color}20;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px ${props => props.color}30;
    }

    .metric-label {
        font-size: 14px;
        color: #595959;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }

    .metric-value {
        font-size: 48px;
        font-weight: 700;
        color: ${props => props.color};
        line-height: 1;
        margin-bottom: 8px;
    }

    .metric-description {
        font-size: 13px;
        color: #8c8c8c;
        margin-top: 8px;
    }
`;

const FullWidthSection = styled(DashboardSection)`
    grid-column: ${props => props.expanded ? '1 / -1' : 'auto'};
`;

const QuickInsightsHeader = styled.div`
    background: linear-gradient(135deg, #FF7F3F 0%, #F16622 100%);
    color: white;
    padding: ${props => props.expanded ? '18px 24px 16px' : '16px'};
    margin: ${props => props.expanded ? '-24px -24px 16px' : '-16px -16px 16px'};
    border-bottom: 2px solid #D14D00;
    box-sizing: border-box;
`;

const QuickInsightsHeadline = styled.div`
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const QuickInsightsStats = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 12px;
    width: 100%;
    box-sizing: border-box;
`;

const QuickInsightStat = styled.div`
    text-align: center;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
    padding: 2px 0;

    &:last-child {
        border-right: none;
    }

    .label {
        font-size: 10px;
        opacity: 0.85;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        margin-bottom: 4px;
        font-weight: 600;
    }

    .value {
        font-size: 22px;
        font-weight: 300;
        line-height: 1.1;
    }
`;

const HealthScoreCircle = styled.div`
    width: 100px;
    height: 100px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: ${props => props.color}15;
    border: 4px solid ${props => props.color};
    margin: 16px auto;
`;

const StatCard = styled(Card)`
    border-radius: 16px;
    border: none;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    position: relative;
    background: white;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${props => props.borderColor || 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'};
    }

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    }

    .ant-card-body {
        padding: 28px;
    }

    .ant-statistic-title {
        font-size: 14px;
        font-weight: 600;
        color: #8c8c8c;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 12px;
    }

    .ant-statistic-content {
        font-size: 42px;
        font-weight: 700;
        color: ${props => props.valueColor || '#262626'};
    }
`;

const InsightCard = styled(Card)`
    border-radius: 12px;
    border: 1px solid ${props => props.borderColor || '#f0f0f0'};
    background: ${props => props.background || 'white'};
    box-shadow: 0 2px 12px ${props => props.shadowColor || 'rgba(0, 0, 0, 0.05)'};
    transition: all 0.3s ease;
    margin-bottom: 16px;

    &:hover {
        box-shadow: 0 4px 20px ${props => props.shadowColor || 'rgba(0, 0, 0, 0.1)'};
        transform: translateX(4px);
    }

    .ant-card-head {
        background: ${props => props.headerBg || 'transparent'};
        border-bottom: 2px solid ${props => props.borderColor || '#f0f0f0'};
        padding: 16px 20px;

        .ant-card-head-title {
            font-size: 16px;
            font-weight: 700;
            color: ${props => props.titleColor || '#262626'};
        }
    }

    .ant-card-body {
        padding: 20px;
        font-size: 15px;
        line-height: 1.8;
    }
`;

const DashboardHeader = styled.div`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 32px;
    border-radius: 20px;
    color: white;
    margin-bottom: 32px;
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);

    h2 {
        color: white;
        margin: 0 0 8px 0;
        font-size: 32px;
        font-weight: 800;
    }

    p {
        color: rgba(255, 255, 255, 0.9);
        margin: 0;
        font-size: 16px;
    }
`;

const GaugeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: linear-gradient(135deg, ${props => props.color}10 0%, ${props => props.color}20 100%);
    border-radius: 16px;
    margin: 16px 0;
`;

const TrendIndicator = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    background: ${props => props.isPositive ? '#f6ffed' : '#fff2e8'};
    color: ${props => props.isPositive ? '#52c41a' : '#fa8c16'};
    font-size: 14px;
    font-weight: 600;
    margin-top: 8px;
`;

const StatusContainer = styled.div`
    text-align: center;
    padding: 32px 16px;
    background: white;
    border-radius: 12px;
    margin: 12px;
    border: 1px solid #f0f0f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const GradientButton = styled(Button)`
    background: linear-gradient(135deg, #FF7F3F 0%, #F16622 100%) !important;
    border: none !important;
    color: white !important;
    height: 44px !important;
    padding: 0 32px !important;
    border-radius: 22px !important;
    font-weight: 600 !important;
    font-size: 15px !important;
    box-shadow: 0 4px 12px rgba(241, 102, 34, 0.3) !important;
    transition: all 0.3s ease !important;

    span {
        color: white !important;
    }

    &:hover, &:focus {
        background: linear-gradient(135deg, #F16622 0%, #D14D00 100%) !important;
        color: white !important;
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(241, 102, 34, 0.4) !important;
        
        span {
            color: white !important;
        }
    }

    &:active {
        transform: translateY(0);
    }
`;

export default function AICopilotPanel() {

    const { instance, accounts } = useMsal();
    const { userDetails } = useUser();
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [chatExpanded, setChatExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [moduleHealth, setModuleHealth] = useState([]);
    const [currentModule, setCurrentModule] = useState(null);
    const [sendingDigest, setSendingDigest] = useState(false);
    const [digest, setDigest] = useState(null);
    const [digestLoading, setDigestLoading] = useState(false);
    const [digestNotFound, setDigestNotFound] = useState(false);
    const [dashboardSpec, setDashboardSpec] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [showDashboard, setShowDashboard] = useState(false);
    const [moduleSidebarSections, setModuleSidebarSections] = useState([]);
    const [activeTab, setActiveTab] = useState('insights');
    const [chatOnly, setChatOnly] = useState(false);
    const isExpanded = chatOnly ? chatExpanded : expanded;
    const drawerTitle = chatOnly
        ? 'Insights AI Assistant'
        : currentModule?.ModuleName
            ? `Module Insights - ${currentModule.ModuleName}`
            : 'Module Insights';

    useEffect(() => {
        if (visible && !chatOnly) {
            loadModuleHealth();
            detectCurrentModule();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, location.pathname, chatOnly]);

    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent('bic:ai-copilot-visibility', {
                detail: { open: visible }
            })
        );
        return () => {
            window.dispatchEvent(
                new CustomEvent('bic:ai-copilot-visibility', {
                    detail: { open: false }
                })
            );
        };
    }, [visible]);

    useEffect(() => {
        // When expanded and we have a current module and digest, load the dashboard
        if (!chatOnly && expanded && currentModule && digest && !showDashboard) {
            // Only load legacy JSON dashboards if it's not an HTML template
            if (!digest.summary?.isHtml) {
                loadDashboard();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded, currentModule, digest, chatOnly]);

    useEffect(() => {
        if (!expanded) {
            setModuleSidebarSections([]);
        }
    }, [expanded, currentModule?.Id, digest?.generatedAt]);

    useEffect(() => {
        const handleOpen = (event) => {
            const tab = event?.detail?.tab;
            setVisible(true);
            if (tab) {
                setActiveTab(tab);
                setChatOnly(tab === 'chat');
            }
        };

        const handleMessage = (event) => {
            if (event.data?.type === 'toggle-width') {
                setExpanded(!!event.data.expanded);
            } else if (event.data?.type === 'regenerate') {
                if (currentModule?.Id) {
                    handleSendTestDigest(currentModule.Id);
                }
            }
        };

        window.addEventListener('bic:open-ai-copilot', handleOpen);
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('bic:open-ai-copilot', handleOpen);
            window.removeEventListener('message', handleMessage);
        };
    }, [currentModule?.Id]);

    const loadModuleHealth = async () => {
        try {
            setLoading(true);
            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const result = await getModuleHealthScores(response.accessToken);

            if (result.success && result.data) {
                setModuleHealth(result.data);
            }
        } catch (error) {
            console.error('Error loading module health:', error);
        } finally {
            setLoading(false);
        }
    };

    const detectCurrentModule = () => {
        // Check if we're on a report page
        if (location.pathname.startsWith('/report/')) {
            const parts = location.pathname.split('/');
            if (parts.length >= 3) {
                const moduleShortName = parts[2];

                // Find the module in user's modules
                const module = userDetails?.Modules?.find(m => m.ShortName === moduleShortName);
                if (module) {
                    const health = moduleHealth.find(h => h.moduleId === module.Id);
                    const updatedModule = {
                        ...module,
                        healthScore: health?.healthScore || 0,
                        lastUpdated: health?.lastUpdated
                    };
                    setCurrentModule(updatedModule);

                    // Fetch digest for current module
                    fetchDigestForModule(module.Id);
                    return;
                }
            }
        }

        setCurrentModule(null);
        setDigest(null);
        setDigestNotFound(false);
        setDashboardSpec(null);
        setShowDashboard(false);
    };

    // ╔══════════════════════════════════════════════════════════════════════╗
    // ║ TEMPLATE SCRIPT INJECTION: Multi-Mode Support & Interactivity         ║
    // ╚══════════════════════════════════════════════════════════════════════╝
    const injectInternalScripts = (html, isExpanded, initialTab = 'visual') => {
        if (!html) return html;

        const interactiveScript = `
        <script>
            (function() {
                console.log('[AICopilot] Internal Template Script Initialized. Expanded:', ${isExpanded}, 'Initial Tab:', '${initialTab}');
                
                // Set Initial Mode based on Parent State
                if (${isExpanded}) {
                    document.body.classList.remove('drawer-mode');
                    document.body.classList.add('wide-mode');
                    const tb = document.getElementById('toggleMode');
                    if (tb) tb.innerText = "Switch to Drawer Mode";
                } else {
                    document.body.classList.remove('wide-mode');
                    document.body.classList.add('drawer-mode');
                    const tb = document.getElementById('toggleMode');
                    if (tb) tb.innerText = "Switch to Wide Mode";
                }

                // Tab Switching Implementation
                window.switchTab = function(tab) {
                    const vTab = document.getElementById('visual-tab');
                    const eTab = document.getElementById('exec-tab');
                    const btns = document.querySelectorAll('.tab-btn');
                    
                    if (vTab && eTab) {
                        if (tab === 'visual') {
                            vTab.classList.remove('hidden');
                            eTab.classList.add('hidden');
                            if (btns[0]) btns[0].classList.add('active');
                            if (btns[1]) btns[1].classList.remove('active');
                        } else {
                            vTab.classList.add('hidden');
                            eTab.classList.remove('hidden');
                            if (btns[1]) btns[1].classList.add('active');
                            if (btns[0]) btns[0].classList.remove('active');
                        }
                    }
                };

                // Apply initial tab
                if ('${initialTab}' === 'executive') {
                    // Small delay to ensure internal tabs are ready
                    setTimeout(() => { if (window.switchTab) window.switchTab('executive'); }, 100);
                }

                // Toggle Mode (Drawer vs Wide) Implementation
                const toggleBtn = document.getElementById('toggleMode');
                if (toggleBtn) {
                    toggleBtn.onclick = function() {
                        const isCurrentlyWide = document.body.classList.contains('wide-mode');
                        if (isCurrentlyWide) {
                            document.body.classList.remove('wide-mode');
                            document.body.classList.add('drawer-mode');
                            toggleBtn.innerText = "Switch to Wide Mode";
                            window.parent.postMessage({ type: 'toggle-width', expanded: false }, '*');
                            window.switchTab('visual');
                        } else {
                            document.body.classList.remove('drawer-mode');
                            document.body.classList.add('wide-mode');
                            toggleBtn.innerText = "Switch to Drawer Mode";
                            window.parent.postMessage({ type: 'toggle-width', expanded: true }, '*');
                        }
                    };
                }
                
                // Global Regenerate Implementation
                window.regenerateDigest = function() {
                    window.parent.postMessage({ type: 'regenerate' }, '*');
                };
            })();
        </script>
        <style>
            .header-bar, .footer-bar, .ui-controls { display: none !important; }
            .content-wrapper, .main-wrapper { 
                padding-top: 10px !important; 
                padding-bottom: 20px !important; 
                max-width: none !important; 
                width: 100% !important; 
                overflow-x: hidden !important;
            }
            body, .drawer-mode, .wide-mode { 
                height: auto !important; 
                min-height: 100% !important; 
                max-width: none !important; 
                width: 100% !important; 
                margin: 0 !important; 
                padding: 0 !important;
                background: white !important;
                overflow-x: hidden !important;
            }
            .visual-tab, .exec-tab, .dashboard-container { 
                max-width: none !important; 
                width: 100% !important; 
                padding: 12px !important; 
                box-sizing: border-box !important;
                margin: 0 !important;
            }
            ::-webkit-scrollbar { width: 6px; }
            ::-webkit-scrollbar-track { background: transparent; }
            ::-webkit-scrollbar-thumb { background: #e8e8e8; border-radius: 10px; }
        </style>
        `;

        if (html.includes('</body>')) {
            return html.replace('</body>', interactiveScript + '</body>');
        }
        return html + interactiveScript;
    };

    const loadDashboard = async () => {
        if (!currentModule) return;

        try {
            setDashboardLoading(true);
            message.info('Generating AI-powered dashboard visualization...');

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const result = await generateModuleDashboard(currentModule.Id, response.accessToken);

            if (result.success && result.data) {
                setDashboardSpec(result.data);
                setShowDashboard(true);
                message.success('Executive dashboard generated successfully!');
            } else if (result.notFound) {
                message.warning('No digest available. Please generate a digest first.');
                setShowDashboard(false);
            } else {
                message.error('Failed to generate dashboard');
                setShowDashboard(false);
            }
        } catch (error) {
            console.error('Error loading dashboard:', error);
            message.error('Failed to load dashboard');
            setShowDashboard(false);
        } finally {
            setDashboardLoading(false);
        }
    };

    const fetchDigestForModule = async (moduleId) => {
        try {
            setDigestLoading(true);
            setDigestNotFound(false);

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const result = await getModuleDigest(moduleId, response.accessToken);

            if (result.notFound) {
                setDigestNotFound(true);
                setDigest(null);
            } else if (result.success) {
                console.log('═══════════════════════════════════════════════════');
                console.log('[AICopilot DEBUG] DIGEST DATA RECEIVED FROM BACKEND');
                console.log('═══════════════════════════════════════════════════');
                console.log('[AICopilot] Full result.data:', JSON.stringify(result.data, null, 2));
                console.log('[AICopilot] Summary object:', result.data?.summary);
                console.log('[AICopilot] Summary keys:', result.data?.summary ? Object.keys(result.data.summary) : 'NO SUMMARY');
                console.log('[AICopilot] --- New AI Fields Check ---');
                console.log('[AICopilot] Has executiveSummary?', !!result.data?.summary?.executiveSummary);
                console.log('[AICopilot] executiveSummary value:', result.data?.summary?.executiveSummary?.substring(0, 100));
                console.log('[AICopilot] Has keyFindings?', !!result.data?.summary?.keyFindings);
                console.log('[AICopilot] keyFindings count:', result.data?.summary?.keyFindings?.length);
                console.log('[AICopilot] Has trendsAnalysis?', !!result.data?.summary?.trendsAnalysis);
                console.log('[AICopilot] trendsAnalysis count:', result.data?.summary?.trendsAnalysis?.length);
                console.log('[AICopilot] --- Old Fields Check (fallback) ---');
                console.log('[AICopilot] Has keyMetrics?', !!result.data?.summary?.keyMetrics);
                console.log('[AICopilot] keyMetrics count:', result.data?.summary?.keyMetrics?.length);
                console.log('[AICopilot] Has trends?', !!result.data?.summary?.trends);
                console.log('[AICopilot] Has recommendations?', !!result.data?.summary?.recommendations);
                console.log('[AICopilot] recommendations count:', result.data?.summary?.recommendations?.length);
                console.log('═══════════════════════════════════════════════════');
                setDigest(result.data);
                setDigestNotFound(false);
            }
        } catch (error) {
            console.error('Error fetching digest:', error);
            setDigestNotFound(true);
            setDigest(null);
        } finally {
            setDigestLoading(false);
        }
    };

    const handleSendTestDigest = async (moduleId) => {
        try {
            setSendingDigest(true);
            setDigestLoading(true);

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const email = accounts[0]?.username;
            await triggerManualDigest(moduleId, email, response.accessToken, true); // Force refresh

            message.success({
                content: 'Generating fresh digest with AI... This may take 60-90 seconds.',
                duration: 8
            });

            // Poll for new digest with exponential backoff
            let attempts = 0;
            const maxAttempts = 100; // Increased from 20 to handle up to 500 seconds (8.3 minutes)
            const pollInterval = 5000; // 5 seconds

            const pollForDigest = async () => {
                attempts++;

                try {
                    const result = await getModuleDigest(moduleId, response.accessToken);

                    if (result.success && result.data) {
                        const generatedTime = new Date(result.data.generatedAt).getTime();
                        const now = Date.now();
                        const ageSeconds = (now - generatedTime) / 1000;

                        // If digest is less than 120 seconds old, it's the new one
                        if (ageSeconds < 120) {
                            setDigest(result.data);
                            setDigestNotFound(false);
                            setDigestLoading(false);
                            setSendingDigest(false);
                            message.success('Fresh digest generated successfully!');
                            return;
                        }
                    }

                    // Continue polling if not found or still old
                    if (attempts < maxAttempts) {
                        setTimeout(pollForDigest, pollInterval);
                    } else {
                        setDigestLoading(false);
                        setSendingDigest(false);
                        message.warning('Digest generation is taking longer than expected. Please refresh manually.');
                    }
                } catch (error) {
                    console.error('Error polling for digest:', error);
                    if (attempts < maxAttempts) {
                        setTimeout(pollForDigest, pollInterval);
                    } else {
                        setDigestLoading(false);
                        setSendingDigest(false);
                        message.error('Failed to fetch new digest. Please try refreshing.');
                    }
                }
            };

            // Start polling after 10 seconds (give backend time to start processing)
            setTimeout(pollForDigest, 10000);

        } catch (error) {
            console.error('Error sending test digest:', error);
            message.error('Failed to send test digest');
            setSendingDigest(false);
            setDigestLoading(false);
        }
    };

    const getHealthColor = (score) => {
        if (score >= 70) return '#52c41a';
        if (score >= 50) return '#faad14';
        return '#ff4d4f';
    };

    const getHealthStatus = (score) => {
        if (score >= 70) return { text: 'Healthy', icon: <CheckCircleOutlined /> };
        if (score >= 50) return { text: 'Warning', icon: <WarningOutlined /> };
        return { text: 'Critical', icon: <WarningOutlined /> };
    };

    const renderDigestContent = (initialTab = 'visual') => {
        if (!digest) return null;

        // ══════════════════════════════════════════════════════════════════════
        // NEW: Support for HTML-based Template Summaries
        // ══════════════════════════════════════════════════════════════════════
        if (digest.summary?.isHtml) {
            return (
                <Card
                    style={{
                        gridColumn: expanded ? '1 / -1' : 'auto',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid #f0f0f0',
                        marginBottom: '24px'
                    }}
                    bodyStyle={{ padding: 0 }}
                >
                    <div
                        className="template-report-container"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'white',
                            minHeight: expanded ? '850px' : '400px'
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                width: '100%',
                                position: 'relative',
                                background: 'white'
                            }}
                        >
                            <iframe
                                title="Module Dashboard Template"
                                srcDoc={injectInternalScripts(digest.summary.htmlContent || digest.summary.executiveSummary, expanded, initialTab)}
                                style={{
                                    width: '100%',
                                    height: expanded ? '850px' : '500px',
                                    border: 'none',
                                    display: 'block'
                                }}
                                sandbox="allow-scripts allow-same-origin allow-popups"
                            />
                        </div>

                        {/* Dashboard Template Footer */}
                        <div style={{
                            padding: '16px 24px',
                            background: '#fafafa',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <RobotOutlined style={{ color: '#F16622', fontSize: '18px' }} />
                                <Text type="secondary" style={{ fontSize: '13px', fontWeight: 500 }}>
                                    Powered by <span style={{ color: '#F16622', fontWeight: 600 }}>Gemini | BIC Engine</span>
                                </Text>
                            </div>

                            <Space size="middle">
                                {digest.generatedAt && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Generated at {new Date(digest.generatedAt).toLocaleString()}
                                    </Text>
                                )}
                                <Button
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        color: '#F16622',
                                        borderColor: '#F16622',
                                        background: 'transparent'
                                    }}
                                    icon={<ReloadOutlined spin={digestLoading} />}
                                    onClick={() => handleSendTestDigest(currentModule.Id)}
                                    loading={digestLoading}
                                >
                                    REGENERATE
                                </Button>
                            </Space>
                        </div>
                    </div>
                </Card>
            );
        }

        return (
            <div style={{ display: 'grid', gridTemplateColumns: expanded ? 'repeat(3, 1fr)' : '1fr', gap: '24px' }}>
                {/* Executive Summary (from enhanced AI prompt) - Full Width */}
                {digest.summary?.executiveSummary && (
                    <InsightCard
                        title={
                            <span>
                                <TrophyOutlined style={{ marginRight: 8 }} />
                                Executive Summary
                            </span>
                        }
                        borderColor="#667eea"
                        background="linear-gradient(135deg, #667eea08 0%, #764ba208 100%)"
                        shadowColor="rgba(102, 126, 234, 0.2)"
                        headerBg="linear-gradient(135deg, #667eea15 0%, #764ba215 100%)"
                        titleColor="#667eea"
                        style={{ gridColumn: expanded ? '1 / -1' : 'auto' }}
                    >
                        <Paragraph
                            style={{
                                fontSize: '16px',
                                marginBottom: 0,
                                lineHeight: '1.9',
                                color: '#262626',
                                fontWeight: 500
                            }}
                        >
                            {digest.summary.executiveSummary}
                        </Paragraph>
                    </InsightCard>
                )}

                {/* Key Findings */}
                {digest.summary?.keyFindings && digest.summary.keyFindings.length > 0 && (
                    <InsightCard
                        title={
                            <span>
                                <BulbOutlined style={{ marginRight: 8 }} />
                                Key Findings ({digest.summary.keyFindings.length})
                            </span>
                        }
                        borderColor="#1890ff"
                        background="linear-gradient(135deg, #1890ff08 0%, #096dd908 100%)"
                        shadowColor="rgba(24, 144, 255, 0.2)"
                        headerBg="linear-gradient(135deg, #1890ff15 0%, #096dd915 100%)"
                        titleColor="#1890ff"
                    >
                        <List
                            size="small"
                            dataSource={digest.summary.keyFindings.slice(0, 6)}
                            renderItem={(item, index) => (
                                <List.Item
                                    key={`finding-${index}`}
                                    style={{
                                        padding: '10px 0',
                                        fontSize: '15px',
                                        border: 'none',
                                        borderBottom: index < digest.summary.keyFindings.slice(0, 6).length - 1 ? '1px solid #e6f7ff' : 'none'
                                    }}
                                >
                                    <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#262626' }}>
                                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                        {typeof item === 'string' ? item : item.finding || item.description || JSON.stringify(item)}
                                    </Text>
                                </List.Item>
                            )}
                        />
                    </InsightCard>
                )}

                {/* Trends Analysis */}
                {digest.summary?.trendsAnalysis && digest.summary.trendsAnalysis.length > 0 && (
                    <InsightCard
                        title={
                            <span>
                                <LineChartOutlined style={{ marginRight: 8 }} />
                                Trends Analysis ({digest.summary.trendsAnalysis.length})
                            </span>
                        }
                        borderColor="#52c41a"
                        background="linear-gradient(135deg, #52c41a08 0%, #389e0d08 100%)"
                        shadowColor="rgba(82, 196, 26, 0.2)"
                        headerBg="linear-gradient(135deg, #52c41a15 0%, #389e0d15 100%)"
                        titleColor="#52c41a"
                    >
                        <List
                            size="small"
                            dataSource={digest.summary.trendsAnalysis.slice(0, 5)}
                            renderItem={(trend, idx) => (
                                <List.Item
                                    key={`trend-${idx}`}
                                    style={{
                                        padding: '10px 0',
                                        fontSize: '15px',
                                        border: 'none',
                                        borderBottom: idx < digest.summary.trendsAnalysis.slice(0, 5).length - 1 ? '1px solid #f6ffed' : 'none'
                                    }}
                                >
                                    <Text style={{ fontSize: '15px', lineHeight: '1.7', color: '#262626' }}>
                                        <RiseOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                        {typeof trend === 'string' ? trend : trend.analysis || trend.description || JSON.stringify(trend)}
                                    </Text>
                                </List.Item>
                            )}
                        />
                    </InsightCard>
                )}

                {/* Key Metrics (fallback) */}
                {digest.summary?.keyMetrics && digest.summary.keyMetrics.length > 0 && (
                    <div>
                        <Text strong style={{ fontSize: '17px', display: 'block', marginBottom: 12, color: '#1890ff', fontWeight: 700 }}>
                            <LineChartOutlined style={{ marginRight: 8, fontSize: '18px' }} />
                            📊 Key Metrics ({digest.summary.keyMetrics.length})
                        </Text>
                        {digest.summary.keyMetrics.slice(0, 3).map((metric, idx) => (
                            <Card
                                key={`key-metric-${idx}`}
                                size="small"
                                style={{
                                    marginBottom: 10,
                                    fontSize: '15px',
                                    borderLeft: '4px solid #1890ff',
                                    background: '#e6f7ff',
                                    padding: '12px',
                                    borderRadius: '0 6px 6px 0',
                                    boxShadow: '0 1px 4px rgba(24, 144, 255, 0.1)'
                                }}
                            >
                                <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 6, color: '#1890ff' }}>
                                    📍 {metric.name || metric.title || `Metric ${idx + 1}`}
                                </Text>
                                <Text strong style={{ fontSize: '20px', display: 'block', color: '#262626', marginBottom: 4 }}>
                                    {metric.value || '—'}
                                </Text>
                                {(metric.description || (typeof metric === 'string' && metric !== metric.value)) && (
                                    <Text style={{ fontSize: '14px', color: '#595959', lineHeight: '1.6' }}>
                                        {metric.description || (typeof metric === 'string' ? metric : '')}
                                    </Text>
                                )}
                            </Card>
                        ))}
                    </div>
                )}

                {/* Considerations */}
                {digest.summary?.recommendations && digest.summary.recommendations.length > 0 && (
                    <InsightCard
                        title={
                            <span>
                                <ThunderboltFilled style={{ marginRight: 8 }} />
                                Considerations ({digest.summary.recommendations.length})
                            </span>
                        }
                        borderColor="#fa8c16"
                        background="linear-gradient(135deg, #fa8c1608 0%, #d4660808 100%)"
                        shadowColor="rgba(250, 140, 22, 0.2)"
                        headerBg="linear-gradient(135deg, #fa8c1615 0%, #d4660815 100%)"
                        titleColor="#fa8c16"
                    >
                        <List
                            size="small"
                            dataSource={digest.summary.recommendations.slice(0, 6)}
                            renderItem={(item, index) => {
                                const priority = typeof item === 'object' ? item.priority : null;
                                const action = typeof item === 'string' ? item : item.action || item.recommendation || item.text || item.description;
                                const rationale = typeof item === 'object' ? item.rationale : null;
                                const priorityColor = priority === 'high' ? '#ff4d4f' : priority === 'medium' ? '#faad14' : '#52c41a';
                                const priorityIcon = priority === 'high' ? <FireOutlined /> : priority === 'medium' ? <WarningOutlined /> : <CheckCircleOutlined />;

                                return (
                                    <List.Item
                                        key={`rec-${index}`}
                                        style={{
                                            padding: '12px 0',
                                            fontSize: '15px',
                                            border: 'none',
                                            borderBottom: index < digest.summary.recommendations.slice(0, 6).length - 1 ? '1px solid #fff7e6' : 'none',
                                            display: 'block'
                                        }}
                                    >
                                        {priority && (
                                            <Badge
                                                color={priorityColor}
                                                text={
                                                    <span style={{ fontSize: '12px', fontWeight: '600', color: priorityColor }}>
                                                        {priorityIcon} {priority.toUpperCase()}
                                                    </span>
                                                }
                                                style={{ marginBottom: '6px' }}
                                            />
                                        )}
                                        <Text strong style={{ fontSize: '15px', display: 'block', lineHeight: '1.7', color: '#262626', marginTop: priority ? '6px' : '0' }}>
                                            {action}
                                        </Text>
                                        {rationale && (
                                            <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginTop: '6px', lineHeight: '1.6' }}>
                                                {rationale}
                                            </Text>
                                        )}
                                    </List.Item>
                                );
                            }}
                        />
                    </InsightCard>
                )}
            </div>
        );
    };

    const renderCurrentModuleContext = () => {
        if (!currentModule) {
            return (
                <FullWidthSection expanded={expanded}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Navigate to a report to see module context"
                    />
                </FullWidthSection>
            );
        }

        const status = getHealthStatus(currentModule.healthScore);
        const color = getHealthColor(currentModule.healthScore);
        const moduleHeadline = `${currentModule.ModuleName} health score ${currentModule.healthScore} with ${status.text.toLowerCase()} status.`;
        const lastUpdatedLabel = currentModule.lastUpdated
            ? new Date(currentModule.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : 'N/A';

        // ══════════════════════════════════════════════════════════════════════
        // NEW: Support for HTML-based Template Summaries
        // Higher priority than legacy React-based summaries
        // ══════════════════════════════════════════════════════════════════════
        if (digest?.summary?.isHtml && digest.summary?.htmlContent) {
            // In expanded mode, we return null here because the main return logic 
            // will call renderDigestContent() which produces a full-width iframe dashboard. 
            // This avoids showing the legacy Metric cards at the top of the dashboard.
            if (expanded) return null;

            return (
                <div
                    className="template-report-container"
                    style={{
                        background: 'white',
                        borderRadius: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        margin: '0 -12px', // Counteract Tabs padding
                        height: '100%',
                        flex: 1,
                        overflow: 'hidden'
                    }}
                >
                    <div
                        style={{
                            padding: 0,
                            flex: 1,
                            overflow: 'hidden',
                            position: 'relative',
                            height: '100%'
                        }}
                    >
                        <iframe
                            title="Module Drawer Template"
                            srcDoc={injectInternalScripts(digest.summary.htmlContent, expanded, 'visual')}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                display: 'block'
                            }}
                            sandbox="allow-scripts allow-same-origin allow-popups"
                        />
                    </div>
                </div>
            );
        }

        // Dashboard view - beautiful multi-section layout (LEGACY JSON MODE)
        if (expanded) {
            // Gauge chart colors based on health score
            const gaugeColors = ['#ff4d4f', '#fa8c16', '#fadb14', '#52c41a'];
            const healthPercent = currentModule.healthScore / 100;

            return (
                <>
                    {/* Hero Section: Gauge + Quick Stats */}
                    <StatCard borderColor={color} valueColor={color} style={{ gridColumn: '1 / -1' }}>
                        <Row gutter={48} align="middle">
                            {/* Left: Gauge Chart */}
                            <Col span={10}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text strong style={{ fontSize: '18px', color: '#262626', display: 'block', marginBottom: '20px' }}>
                                        <DashboardOutlined style={{ marginRight: 8, color: '#667eea' }} />
                                        {currentModule.ModuleName}
                                    </Text>
                                    <GaugeContainer color={color}>
                                        <GaugeChart
                                            id={`gauge-${currentModule.Id}`}
                                            nrOfLevels={4}
                                            colors={gaugeColors}
                                            arcWidth={0.3}
                                            percent={healthPercent}
                                            textColor={color}
                                            needleColor="#666"
                                            needleBaseColor="#999"
                                            hideText={true}
                                            style={{ width: '280px' }}
                                        />
                                        <div style={{ marginTop: '-30px', textAlign: 'center' }}>
                                            <div style={{ fontSize: '72px', fontWeight: '900', color, lineHeight: '1' }}>
                                                {currentModule.healthScore}
                                            </div>
                                            <Text type="secondary" style={{ fontSize: '16px', fontWeight: '600' }}>Health Score</Text>
                                        </div>
                                    </GaugeContainer>
                                    <Tag icon={status.icon} color={color} style={{ fontSize: '16px', padding: '10px 20px', marginTop: '20px' }}>
                                        {status.text}
                                    </Tag>
                                </div>
                            </Col>

                            {/* Right: Quick Stats Grid */}
                            <Col span={14}>
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <div style={{
                                            padding: '24px',
                                            background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                            borderRadius: '12px',
                                            borderLeft: '4px solid #667eea',
                                            height: '100%'
                                        }}>
                                            <div style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: '600', marginBottom: '8px' }}>
                                                MODULE STATUS
                                            </div>
                                            <div style={{ fontSize: '28px', fontWeight: '700', color: color, marginBottom: '4px' }}>
                                                {status.text}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#595959' }}>
                                                Current Performance Level
                                            </div>
                                        </div>
                                    </Col>

                                    <Col span={12}>
                                        <div style={{
                                            padding: '24px',
                                            background: 'linear-gradient(135deg, #1890ff15 0%, #096dd915 100%)',
                                            borderRadius: '12px',
                                            borderLeft: '4px solid #1890ff',
                                            height: '100%'
                                        }}>
                                            <div style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: '600', marginBottom: '8px' }}>
                                                LAST UPDATED
                                            </div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1890ff', marginBottom: '4px' }}>
                                                {currentModule.lastUpdated
                                                    ? new Date(currentModule.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                    : 'N/A'
                                                }
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#595959' }}>
                                                Data Refresh Date
                                            </div>
                                        </div>
                                    </Col>

                                    {digest && (
                                        <>
                                            <Col span={12}>
                                                <div style={{
                                                    padding: '24px',
                                                    background: 'linear-gradient(135deg, #52c41a15 0%, #389e0d15 100%)',
                                                    borderRadius: '12px',
                                                    borderLeft: '4px solid #52c41a',
                                                    height: '100%'
                                                }}>
                                                    <div style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: '600', marginBottom: '8px' }}>
                                                        KEY FINDINGS
                                                    </div>
                                                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#52c41a', marginBottom: '4px' }}>
                                                        {digest.summary?.keyFindings?.length || 0}
                                                    </div>
                                                    <div style={{ fontSize: '13px', color: '#595959' }}>
                                                        Insights Identified
                                                    </div>
                                                </div>
                                            </Col>

                                            <Col span={12}>
                                                <div style={{
                                                    padding: '24px',
                                                    background: 'linear-gradient(135deg, #fa8c1615 0%, #d4660815 100%)',
                                                    borderRadius: '12px',
                                                    borderLeft: '4px solid #fa8c16',
                                                    height: '100%'
                                                }}>
                                                    <div style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: '600', marginBottom: '8px' }}>
                                                        RECOMMENDATIONS
                                                    </div>
                                                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#fa8c16', marginBottom: '4px' }}>
                                                        {digest.summary?.recommendations?.length || 0}
                                                    </div>
                                                    <div style={{ fontSize: '13px', color: '#595959' }}>
                                                        Action Items
                                                    </div>
                                                </div>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </Col>
                        </Row>
                    </StatCard>
                </>
            );
        }


        // ╔══════════════════════════════════════════════════════════════════════╗
        // ║ IDLE / NOT FOUND STATE: Match Page Summary Drawer Idle View          ║
        // ╚══════════════════════════════════════════════════════════════════════╝
        if (digestNotFound || !digest) {
            return (
                <StatusContainer>
                    <div style={{ marginBottom: 24, marginTop: 12 }}>
                        <RobotOutlined style={{
                            fontSize: 64,
                            background: 'linear-gradient(135deg, #FF7F3F 0%, #F16622 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            filter: 'drop-shadow(0 4px 8px rgba(241, 102, 34, 0.2))'
                        }} />
                    </div>
                    <Title level={4} style={{ marginBottom: 12, fontWeight: 700 }}>Module Intelligence</Title>
                    <Paragraph type="secondary" style={{ maxWidth: 360, margin: '0 auto 24px', fontSize: '15px', lineHeight: '1.6' }}>
                        Get an AI-powered digest for <strong>{currentModule.ModuleName}</strong> including
                        strategic health scores, key findings, and action items.
                    </Paragraph>
                    <GradientButton
                        size="large"
                        icon={<ThunderboltFilled />}
                        loading={sendingDigest || digestLoading}
                        onClick={() => handleSendTestDigest(currentModule.Id)}
                    >
                        Generate Module Digest
                    </GradientButton>

                    {digestLoading && (
                        <div style={{ marginTop: 24 }}>
                            <Spin size="small" />
                            <Text type="secondary" style={{ marginLeft: 8, fontSize: '13px' }}>
                                Analyzing module data...
                            </Text>
                        </div>
                    )}
                </StatusContainer>
            );
        }

        const sidebarSections = [];

        // Only add Executive Summary section in legacy mode (not HTML template mode)
        if (digest?.summary?.executiveSummary) {
            sidebarSections.push({
                key: 'executive-summary',
                title: 'Executive Summary',
                icon: <TrophyOutlined style={{ color: '#667eea' }} />,
                color: '#667eea',
                count: null,
                content: (
                    <Card
                        size="small"
                        style={{
                            fontSize: '15px',
                            background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
                            border: '2px solid #667eea',
                            padding: '14px',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                            borderRadius: '8px'
                        }}
                    >
                        <Paragraph
                            style={{
                                fontSize: '15px',
                                marginBottom: 0,
                                lineHeight: '1.8',
                                color: '#262626',
                                fontWeight: 500
                            }}
                        >
                            {digest.summary.executiveSummary}
                        </Paragraph>
                    </Card>
                )
            });
        }

        if (digest?.summary?.keyFindings?.length) {
            sidebarSections.push({
                key: 'key-findings',
                title: 'Key Findings',
                icon: <BulbOutlined style={{ color: '#1890ff' }} />,
                color: '#1890ff',
                count: digest.summary.keyFindings.length,
                content: (
                    <List
                        size="small"
                        dataSource={digest.summary.keyFindings.slice(0, 5)}
                        renderItem={(item, index) => (
                            <List.Item
                                key={`finding-${index}`}
                                style={{
                                    padding: '12px 14px',
                                    fontSize: '15px',
                                    borderLeft: '4px solid #1890ff',
                                    marginBottom: 8,
                                    background: '#e6f7ff',
                                    borderRadius: '0 6px 6px 0',
                                    boxShadow: '0 1px 4px rgba(24, 144, 255, 0.1)'
                                }}
                            >
                                <Text strong style={{ fontSize: '15px', lineHeight: '1.7', color: '#262626' }}>
                                    ✓ {typeof item === 'string' ? item : item.finding || item.description || JSON.stringify(item)}
                                </Text>
                            </List.Item>
                        )}
                    />
                )
            });
        }

        if (digest?.summary?.trendsAnalysis?.length) {
            sidebarSections.push({
                key: 'trends-analysis',
                title: 'Trends Analysis',
                icon: <LineChartOutlined style={{ color: '#52c41a' }} />,
                color: '#52c41a',
                count: digest.summary.trendsAnalysis.length,
                content: (
                    <>
                        {digest.summary.trendsAnalysis.slice(0, 3).map((trend, idx) => (
                            <Card
                                key={`trend-analysis-${idx}`}
                                size="small"
                                style={{
                                    marginBottom: 10,
                                    fontSize: '15px',
                                    borderLeft: '4px solid #52c41a',
                                    background: '#f6ffed',
                                    padding: '10px',
                                    borderRadius: '0 6px 6px 0',
                                    boxShadow: '0 1px 4px rgba(82, 196, 26, 0.1)'
                                }}
                            >
                                <Text strong style={{ fontSize: '15px', lineHeight: '1.7', color: '#262626' }}>
                                    📊 {typeof trend === 'string' ? trend : trend.analysis || trend.description || JSON.stringify(trend)}
                                </Text>
                            </Card>
                        ))}
                    </>
                )
            });
        }

        if (digest?.summary?.keyMetrics?.length) {
            sidebarSections.push({
                key: 'key-metrics',
                title: 'Key Metrics',
                icon: <LineChartOutlined style={{ color: '#1890ff' }} />,
                color: '#1890ff',
                count: digest.summary.keyMetrics.length,
                content: (
                    <>
                        {digest.summary.keyMetrics.slice(0, 3).map((metric, idx) => (
                            <Card
                                key={`key-metric-${idx}`}
                                size="small"
                                style={{
                                    marginBottom: 10,
                                    fontSize: '15px',
                                    borderLeft: '4px solid #1890ff',
                                    background: '#e6f7ff',
                                    padding: '12px',
                                    borderRadius: '0 6px 6px 0',
                                    boxShadow: '0 1px 4px rgba(24, 144, 255, 0.1)'
                                }}
                            >
                                <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 6, color: '#1890ff' }}>
                                    📍 {metric.name || metric.title || `Metric ${idx + 1}`}
                                </Text>
                                <Text strong style={{ fontSize: '20px', display: 'block', color: '#262626', marginBottom: 4 }}>
                                    {metric.value || '—'}
                                </Text>
                                {(metric.description || (typeof metric === 'string' && metric !== metric.value)) && (
                                    <Text style={{ fontSize: '14px', color: '#595959', lineHeight: '1.6' }}>
                                        {metric.description || (typeof metric === 'string' ? metric : '')}
                                    </Text>
                                )}
                            </Card>
                        ))}
                    </>
                )
            });
        }

        if (digest?.summary?.trends?.length) {
            sidebarSections.push({
                key: 'trends',
                title: 'Trends',
                icon: <LineChartOutlined style={{ color: '#52c41a' }} />,
                color: '#52c41a',
                count: digest.summary.trends.length,
                content: (
                    <>
                        {digest.summary.trends.slice(0, 3).map((trend, idx) => (
                            <Card
                                key={`old-trend-${idx}`}
                                size="small"
                                style={{
                                    marginBottom: 8,
                                    fontSize: '14px',
                                    borderLeft: '3px solid #52c41a'
                                }}
                            >
                                <Text strong style={{ fontSize: '14px' }}>
                                    {trend.title || trend.category || trend.name || `Trend ${idx + 1}`}
                                </Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '14px' }}>
                                    {trend.description || trend.value || trend.insight || (typeof trend === 'string' ? trend : JSON.stringify(trend))}
                                </Text>
                            </Card>
                        ))}
                    </>
                )
            });
        }

        if (digest?.summary?.recommendations?.length) {
            sidebarSections.push({
                key: 'considerations',
                title: 'Considerations',
                icon: <CheckCircleOutlined style={{ color: '#fa8c16' }} />,
                color: '#fa8c16',
                count: digest.summary.recommendations.length,
                content: (
                    <>
                        {digest.summary.recommendations.slice(0, 5).map((item, index) => {
                            const priority = typeof item === 'object' ? item.priority : null;
                            const action = typeof item === 'string' ? item : item.action || item.recommendation || item.text || item.description;
                            const rationale = typeof item === 'object' ? item.rationale : null;

                            const priorityColor = priority === 'high' ? '#ff4d4f' : priority === 'medium' ? '#faad14' : '#52c41a';
                            const priorityBg = priority === 'high' ? '#fff1f0' : priority === 'medium' ? '#fffbe6' : '#f6ffed';
                            const priorityEmoji = priority === 'high' ? '🔴' : priority === 'medium' ? '🟡' : '🟢';

                            return (
                                <Card
                                    key={`rec-${index}-${typeof action === 'string' ? action.substring(0, 20) : index}`}
                                    size="small"
                                    style={{
                                        marginBottom: 10,
                                        fontSize: '15px',
                                        borderLeft: `4px solid ${priorityColor}`,
                                        background: priorityBg,
                                        padding: '12px',
                                        borderRadius: '0 6px 6px 0',
                                        boxShadow: `0 1px 4px ${priorityColor}20`
                                    }}
                                >
                                    {priority && (
                                        <Tag color={priorityColor} style={{ fontSize: '14px', marginBottom: 8, fontWeight: 600, padding: '4px 10px' }}>
                                            {priorityEmoji} {priority.toUpperCase()} PRIORITY
                                        </Tag>
                                    )}
                                    <Text strong style={{ fontSize: '15px', display: 'block', lineHeight: '1.7', color: '#262626' }}>
                                        ▸ {action}
                                    </Text>
                                    {rationale && (
                                        <Text style={{ fontSize: '14px', display: 'block', marginTop: 8, color: '#595959', lineHeight: '1.6' }}>
                                            💭 {rationale}
                                        </Text>
                                    )}
                                </Card>
                            );
                        })}
                    </>
                )
            });
        }

        const sidebarSectionKeys = sidebarSections.map((section) => section.key);
        const sidebarAllExpanded = sidebarSectionKeys.length > 0
            && moduleSidebarSections.length === sidebarSectionKeys.length;

        return (
            <DashboardSection expanded={expanded}>
                <QuickInsightsHeader expanded={expanded}>
                    <QuickInsightsHeadline>{moduleHeadline}</QuickInsightsHeadline>
                    <QuickInsightsStats>
                        <QuickInsightStat>
                            <div className="label">Health Score</div>
                            <div className="value">{currentModule.healthScore}</div>
                        </QuickInsightStat>
                        <QuickInsightStat>
                            <div className="label">Status</div>
                            <div className="value" style={{ fontSize: '18px', fontWeight: 500 }}>
                                {status.text}
                            </div>
                        </QuickInsightStat>
                        <QuickInsightStat>
                            <div className="label">Last Updated</div>
                            <div className="value" style={{ fontSize: '16px', fontWeight: 500 }}>
                                {lastUpdatedLabel}
                            </div>
                        </QuickInsightStat>
                    </QuickInsightsStats>
                </QuickInsightsHeader>

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {/* Legacy/Sidebar Manual Regenerate Button */}
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        block
                        size="large"
                        loading={sendingDigest || digestLoading}
                        onClick={() => handleSendTestDigest(currentModule.Id)}
                        style={{ marginTop: 12 }}
                    >
                        Regenerate AI Digest
                    </Button>

                    <Divider style={{ margin: '8px 0' }} />

                    <div style={{ padding: '0 4px' }}>
                        <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 12 }}>
                            <BulbOutlined style={{ color: '#1890ff', marginRight: 8, fontSize: '16px' }} />
                            AI-Generated Insights & Summary
                        </Text>

                        {sidebarSectionKeys.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                <Tooltip title={sidebarAllExpanded ? "Collapse all sections" : "Expand all sections"}>
                                    <Button
                                        type="text"
                                        icon={
                                            <DoubleRightOutlined
                                                style={{
                                                    transform: sidebarAllExpanded ? 'rotate(90deg)' : 'none',
                                                    transition: 'transform 0.15s ease'
                                                }}
                                            />
                                        }
                                        onClick={() => {
                                            setModuleSidebarSections(
                                                sidebarAllExpanded ? [] : sidebarSectionKeys
                                            );
                                        }}
                                        style={{ color: '#F16622', fontSize: 16, marginRight: -4 }}
                                    />
                                </Tooltip>
                            </div>
                        )}

                        <SidebarCollapse
                            activeKey={moduleSidebarSections}
                            onChange={(keys) => {
                                const nextKeys = Array.isArray(keys) ? keys : keys ? [keys] : [];
                                setModuleSidebarSections(nextKeys);
                            }}
                            ghost
                            expandIconPosition="end"
                        >
                            {sidebarSections.map((section) => (
                                <Panel
                                    header={
                                        <SidebarSectionHeader>
                                            <div className="title">
                                                <span className="icon">{section.icon}</span>
                                                <span>{section.title}</span>
                                            </div>
                                            {typeof section.count === 'number' && (
                                                <Badge count={section.count} style={{ backgroundColor: section.color }} />
                                            )}
                                        </SidebarSectionHeader>
                                    }
                                    key={section.key}
                                >
                                    {section.content}
                                </Panel>
                            ))}
                        </SidebarCollapse>
                    </div>

                    {digest.generatedAt && (
                        <Text
                            type="secondary"
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                fontSize: '12px',
                                marginTop: 16,
                                color: '#999'
                            }}
                        >
                            Last updated: {new Date(digest.generatedAt).toLocaleString()}
                        </Text>
                    )}
                </Space>
            </DashboardSection>
        );
    };

    const renderAllModulesOverview = () => {
        if (loading) {
            return (
                <DashboardSection expanded={expanded}>
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                        <Spin size="large" />
                    </div>
                </DashboardSection>
            );
        }

        // Use mock data for visual testing if no real data available
        const displayModules = moduleHealth.length === 0 ? [
            { moduleId: 1, moduleName: 'Human Resources', healthScore: 85 },
            { moduleId: 2, moduleName: 'Finance', healthScore: 72 },
            { moduleId: 3, moduleName: 'HRST', healthScore: 45 },
            { moduleId: 4, moduleName: 'Incident Management', healthScore: 90 },
            { moduleId: 5, moduleName: 'Sales', healthScore: 68 }
        ] : moduleHealth;

        return (
            <DashboardSection expanded={expanded} title={
                <Space>
                    <ThunderboltFilled style={{ color: '#faad14' }} />
                    <span>All Modules</span>
                </Space>
            }>
                <List
                    size="small"
                    dataSource={displayModules}
                    renderItem={(module) => {
                        const color = getHealthColor(module.healthScore);
                        return (
                            <List.Item
                                style={{
                                    padding: '12px 0',
                                    borderBottom: '1px solid #f0f0f0'
                                }}
                            >
                                <div style={{ width: '100%' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: 8
                                    }}>
                                        <Text strong>{module.moduleName}</Text>
                                        <Badge
                                            count={module.healthScore}
                                            style={{
                                                backgroundColor: color,
                                                fontSize: '15px'
                                            }}
                                            overflowCount={100}
                                        />
                                    </div>
                                    <Progress
                                        percent={module.healthScore}
                                        strokeColor={color}
                                        size="small"
                                        showInfo={false}
                                    />
                                </div>
                            </List.Item>
                        );
                    }}
                />
            </DashboardSection>
        );
    };

    const renderQuickInsights = () => {
        // Use mock data for visual testing if no real data available
        const displayModules = moduleHealth.length === 0 ? [
            { moduleId: 1, moduleName: 'Human Resources', healthScore: 85 },
            { moduleId: 2, moduleName: 'Finance', healthScore: 72 },
            { moduleId: 3, moduleName: 'HRST', healthScore: 45 },
            { moduleId: 4, moduleName: 'Incident Management', healthScore: 90 },
            { moduleId: 5, moduleName: 'Sales', healthScore: 68 }
        ] : moduleHealth;

        const criticalModules = displayModules.filter(m => m.healthScore < 50);
        const healthyModules = displayModules.filter(m => m.healthScore >= 70);
        const averageScore = displayModules.length > 0
            ? Math.round(displayModules.reduce((sum, m) => sum + m.healthScore, 0) / displayModules.length)
            : 0;
        const headlineParts = [
            criticalModules.length > 0
                ? `${criticalModules.length} module${criticalModules.length > 1 ? 's' : ''} need attention`
                : null,
            healthyModules.length > 0
                ? `${healthyModules.length} module${healthyModules.length > 1 ? 's' : ''} performing well`
                : null,
            displayModules.length > 0 ? `Overall average ${averageScore}%` : null
        ].filter(Boolean);
        const headlineText = headlineParts.length > 0
            ? headlineParts.join(' • ')
            : 'No module health data available yet.';

        return (
            <DashboardSection expanded={expanded} title={
                <Space>
                    <BulbOutlined style={{ color: '#1890ff' }} />
                    <span>Quick Insights</span>
                </Space>
            }>
                <QuickInsightsHeader expanded={expanded}>
                    <QuickInsightsHeadline>{headlineText}</QuickInsightsHeadline>
                    <QuickInsightsStats>
                        <QuickInsightStat>
                            <div className="label">Needs Attention</div>
                            <div className="value">{criticalModules.length}</div>
                        </QuickInsightStat>
                        <QuickInsightStat>
                            <div className="label">Healthy Modules</div>
                            <div className="value">{healthyModules.length}</div>
                        </QuickInsightStat>
                        <QuickInsightStat>
                            <div className="label">Avg Health</div>
                            <div className="value">{averageScore}%</div>
                        </QuickInsightStat>
                    </QuickInsightsStats>
                </QuickInsightsHeader>
            </DashboardSection>
        );
    };

    const renderFooter = () => {
        if (chatOnly || !digest || activeTab !== 'insights') return null;

        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <RobotOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                    <Text type="secondary" style={{ fontSize: '11px', fontWeight: 500 }}>
                        Powered by <span style={{ color: '#F16622', fontWeight: 600 }}>Gemini | BIC Engine</span>
                    </Text>
                </div>

                <Space size="small">
                    {digest?.generatedAt && (
                        <Text type="secondary" style={{ fontSize: '10px', marginRight: 4 }}>
                            {new Date(digest.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                    )}
                    <Button
                        type="primary"
                        ghost
                        size="small"
                        icon={<ReloadOutlined spin={digestLoading} />}
                        onClick={() => currentModule?.Id && handleSendTestDigest(currentModule.Id)}
                        loading={digestLoading}
                        style={{
                            borderRadius: '16px',
                            fontSize: '11px',
                            fontWeight: 700,
                            height: '28px',
                            padding: '0 12px',
                            borderColor: '#F97316',
                            color: '#F97316'
                        }}
                    >
                        REGENERATE
                    </Button>
                </Space>
            </div>
        );
    };

    return (
        <>
            <StyledDrawer
                title={
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <RobotOutlined style={{ color: '#667eea', fontSize: '20px', marginTop: '2px' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{
                                fontSize: '15px',
                                fontWeight: 600,
                                color: '#1a1a1a',
                                letterSpacing: '-0.01em',
                                lineHeight: '1.3'
                            }}>
                                {drawerTitle}
                            </span>
                            {!chatOnly && expanded && (
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: '#666',
                                    letterSpacing: '-0.005em',
                                    lineHeight: '1.3'
                                }}>
                                    Dashboard view
                                </span>
                            )}
                        </div>
                    </div>
                }
                placement="right"
                onClose={() => {
                    setVisible(false);
                    setExpanded(false);
                    setChatExpanded(false);
                    setShowDashboard(false);
                    setActiveTab('insights'); // Reset to insights tab
                    setChatOnly(false);
                }}
                open={visible}
                width={isExpanded ? '92%' : 820}
                closeIcon={<CloseOutlined />}
                styles={{
                    body: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column' },
                    footer: {
                        padding: '10px 20px',
                        background: 'white',
                        borderTop: '1px solid #f0f0f0',
                        boxShadow: '0 -2px 8px rgba(0,0,0,0.05)'
                    }
                }}
                footer={renderFooter()}
                extra={
                    <Space>
                        {expanded && currentModule && digest && activeTab === 'insights' && (
                            <Tooltip title={showDashboard ? "Show AI Summary" : "Show Executive Dashboard"}>
                                <Button
                                    type="text"
                                    icon={showDashboard ? <BulbOutlined /> : <DashboardOutlined />}
                                    onClick={() => setShowDashboard(!showDashboard)}
                                    style={{ fontSize: '18px', color: '#F16622' }}
                                    loading={dashboardLoading}
                                />
                            </Tooltip>
                        )}
                        {(activeTab === 'insights' || activeTab === 'chat') && (
                            <Tooltip
                                title={chatOnly
                                    ? (isExpanded ? "Collapse" : "Expand")
                                    : (expanded ? "Collapse to sidebar" : "Expand to dashboard")
                                }
                            >
                                <Button
                                    type="text"
                                    icon={isExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                                    onClick={() => {
                                        if (chatOnly) {
                                            setChatExpanded(!chatExpanded);
                                        } else {
                                            setExpanded(!expanded);
                                        }
                                    }}
                                    style={{ fontSize: '18px', color: '#667eea' }}
                                />
                            </Tooltip>
                        )}
                        {activeTab === 'insights' && !chatOnly && (
                            <Tooltip title="Refresh data">
                                <Button
                                    type="text"
                                    icon={<ReloadOutlined />}
                                    onClick={loadModuleHealth}
                                    loading={loading}
                                />
                            </Tooltip>
                        )}
                    </Space>
                }
            >
                {chatOnly ? (
                    <div style={{ flex: 1, minHeight: 0, padding: '0 12px', display: 'flex' }}>
                        <AICopilotChatV2 compact={!isExpanded} />
                    </div>
                ) : (
                    <StyledTabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        destroyInactiveTabPane={false}
                        items={[
                            {
                                key: 'insights',
                                label: (
                                    <span>
                                        <BulbOutlined /> Quick Insights
                                    </span>
                                ),
                                children: expanded ? (
                                    showDashboard && dashboardSpec ? (
                                        // Show AI-Generated Executive Dashboard
                                        <AIDashboardReport dashboardSpec={dashboardSpec} />
                                    ) : (
                                        // Show normal expanded view
                                        <DashboardGrid expanded={expanded}>
                                            {/* Row 1: Three metric cards */}
                                            {renderCurrentModuleContext()}

                                            {/* Row 2: AI Summary (full width) - only show if on a specific module */}
                                            {currentModule && digest && (
                                                digest?.summary?.isHtml ? (
                                                    // For modern HTML templates, render the content directly (it has its own card/styling)
                                                    renderDigestContent('visual')
                                                ) : (
                                                    // For legacy JSON summaries, wrap in the standard full-width section with title
                                                    <FullWidthSection expanded={expanded} title="AI-Generated Insights & Considerations">
                                                        {renderDigestContent('visual')}
                                                    </FullWidthSection>
                                                )
                                            )}

                                            {/* Row 3: Quick Insights and All Modules - only show if NOT on a specific module */}
                                            {!currentModule && renderQuickInsights()}
                                            {!currentModule && renderAllModulesOverview()}
                                        </DashboardGrid>
                                    )
                                ) : (
                                    <>
                                        {renderCurrentModuleContext()}

                                        {/* Only show these sections in sidebar when NOT viewing a specific module */}
                                        {!currentModule && renderQuickInsights()}
                                        {!currentModule && renderAllModulesOverview()}
                                    </>
                                )
                            },
                            ...(expanded && currentModule && digest?.summary?.isHtml ? [
                                {
                                    key: 'executive',
                                    label: (
                                        <span>
                                            <TrophyOutlined /> Executive Report
                                        </span>
                                    ),
                                    children: (
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white' }}>
                                            {renderDigestContent('executive')}
                                        </div>
                                    )
                                }
                            ] : []),
                            ...(expanded ? [
                                {
                                    key: 'chat',
                                    label: (
                                        <span>
                                            <CommentOutlined /> AI Assistant
                                        </span>
                                    ),
                                    children: (
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px', background: '#f5f5f5' }}>
                                            <AICopilotChatV2 compact={false} />
                                        </div>
                                    )
                                }
                            ] : [])
                        ]}
                    />
                )}
            </StyledDrawer>
        </>
    );
}
